import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { playersAPI, teamsAPI, API_URL } from '../services/api';
import './Players.css';

interface Team {
  id: string;
  name: string;
  division?: string;
  ageGroup?: string;
}

interface Player {
  id: string;
  firstName: string;
  lastName: string;
  position?: string;
  jerseyNumber?: number;
  dateOfBirth?: string;
  photoUrl?: string;
  height?: string;
  weight?: string;
  guardian1Name?: string;
  guardian1Email?: string;
  guardian1Phone?: string;
  guardian1Password?: string;
  guardian2Name?: string;
  guardian2Email?: string;
  guardian2Phone?: string;
  guardian2Password?: string;
  playerType?: string;
  team?: {
    id: string;
    name: string;
  };
  teams?: Array<{
    id: string;
    name: string;
  }>;
}

const Players: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [creatingPlayer, setCreatingPlayer] = useState(false);
  const [newPlayer, setNewPlayer] = useState<Partial<Player>>({});
  const [newPlayerPhoto, setNewPlayerPhoto] = useState<File | null>(null);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const teamFilter = searchParams.get('team');

  useEffect(() => {
    loadPlayers();
    loadTeams();
  }, []);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      const response = await playersAPI.getAll();
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to load players:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeams = async () => {
    try {
      const response = await teamsAPI.getAll();
      setAllTeams(response.data);
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const handleCardClick = (playerId: string) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const handleEdit = (player: Player, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingPlayer(player);
    // Initialize selected teams
    const currentTeams: string[] = [];
    if (player.team) {
      currentTeams.push(player.team.id);
    }
    if (player.teams) {
      currentTeams.push(...player.teams.map(t => t.id));
    }
    setSelectedTeamIds(Array.from(new Set(currentTeams))); // Remove duplicates
  };

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeamIds(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const handlePhotoUpload = async (playerId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await playersAPI.uploadPhoto(playerId, file);
      await loadPlayers(); // Reload to show new photo
      alert('Photo uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Failed to upload photo');
    }
  };

  const handleSave = async () => {
    if (!editingPlayer) return;
    
    try {
      // Prepare update data with team IDs
      const updateData = {
        ...editingPlayer,
        teamIds: selectedTeamIds,  // Send selected team IDs
      };
      
      await playersAPI.update(editingPlayer.id, updateData);
      await loadPlayers();
      setEditingPlayer(null);
      setSelectedTeamIds([]);
    } catch (error: any) {
      console.error('Failed to update player:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update player';
      alert(`Failed to update player: ${errorMessage}`);
    }
  };

  const handleDelete = async (player: Player) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${player.firstName} ${player.lastName}? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    try {
      await playersAPI.delete(player.id);
      await loadPlayers();
      setEditingPlayer(null);
      alert('Player deleted successfully');
    } catch (error) {
      console.error('Failed to delete player:', error);
      alert('Failed to delete player');
    }
  };

  const filteredPlayers = players.filter(player => {
    const matchesSearch = `${player.firstName} ${player.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.position?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTeam = !teamFilter || 
      player.team?.id === teamFilter ||
      player.teams?.some(t => t.id === teamFilter);
    
    return matchesSearch && matchesTeam;
  });

  if (loading) {
    return <div className="loading">Loading players...</div>;
  }

  return (
    <div className="players-page">
      <div className="hero-section">
        <h1>Our Players</h1>
        <p>Meet the talented athletes of STARS Academy - Click cards to see details</p>
      </div>

      <div className="players-container">
        <div className="players-header">
          <h2>Player Roster {teamFilter && `- Filtered by Team`}</h2>
          <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
            <input
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button 
              onClick={() => {
                setCreatingPlayer(true);
                setNewPlayer({});
                setNewPlayerPhoto(null);
                setSelectedTeamIds([]);
              }}
              className="btn-save"
              style={{whiteSpace: 'nowrap'}}
            >
              + Add Player
            </button>
          </div>
        </div>

        <div className="players-grid">
          {filteredPlayers.map((player) => (
            <div 
              key={player.id} 
              className={`player-card-container ${flippedCards.has(player.id) ? 'flipped' : ''}`}
              onClick={() => handleCardClick(player.id)}
            >
              <div className="player-card-inner">
                {/* Front of card */}
                <div className="player-card player-card-front">
                  <div className="player-image">
                    {player.photoUrl ? (
                      <img 
                        src={`${API_URL}${player.photoUrl}`} 
                        alt={`${player.firstName} ${player.lastName}`}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback) (fallback as HTMLElement).classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`player-initial ${player.photoUrl ? 'hidden' : ''}`}>
                      {player.firstName[0]}{player.lastName[0]}
                    </div>
                    {player.jerseyNumber && (
                      <div className="jersey-number">#{player.jerseyNumber}</div>
                    )}
                    <label 
                      className="upload-photo-btn"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.9)';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      📷
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handlePhotoUpload(player.id, e)}
                      />
                    </label>
                  </div>
                  <div className="player-info">
                    <h3>{player.firstName} {player.lastName}</h3>
                    {player.position && <p className="position">{player.position}</p>}
                    {player.team && (
                      <p className="team">{player.team.name}</p>
                    )}
                    {player.teams && player.teams.length > 0 && (
                      <p className="teams-list">
                        {player.teams.map(t => t.name).join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Back of card */}
                <div className="player-card player-card-back">
                  <button 
                    className="edit-btn"
                    onClick={(e) => handleEdit(player, e)}
                  >
                    ✏️ View/Edit Details
                  </button>
                  <div className="player-details">
                    <h3>{player.firstName} {player.lastName}</h3>
                    
                    <div className="detail-section">
                      <h4>Player Info</h4>
                      <p><strong>Position:</strong> {player.position || 'N/A'}</p>
                      <p><strong>Jersey #:</strong> {player.jerseyNumber || 'N/A'}</p>
                      <p><strong>Height:</strong> {player.height || 'N/A'}</p>
                      <p><strong>Weight:</strong> {player.weight || 'N/A'}</p>
                      {player.dateOfBirth && (
                        <p><strong>DOB:</strong> {new Date(player.dateOfBirth).toLocaleDateString()}</p>
                      )}
                    </div>

                    {(player.guardian1Name || player.guardian2Name) && (
                      <div className="detail-section">
                        <h4>Guardian Information</h4>
                        {player.guardian1Name && (
                          <div className="guardian-info">
                            <p><strong>Guardian 1:</strong> {player.guardian1Name}</p>
                            {player.guardian1Email && <p>📧 {player.guardian1Email}</p>}
                            {player.guardian1Phone && <p>📞 {player.guardian1Phone}</p>}
                          </div>
                        )}
                        {player.guardian2Name && (
                          <div className="guardian-info">
                            <p><strong>Guardian 2:</strong> {player.guardian2Name}</p>
                            {player.guardian2Email && <p>📧 {player.guardian2Email}</p>}
                            {player.guardian2Phone && <p>📞 {player.guardian2Phone}</p>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredPlayers.length === 0 && (
          <div className="no-data">No players found</div>
        )}
      </div>

      {/* Create Player Modal */}
      {creatingPlayer && (
        <div className="modal-overlay" onClick={() => setCreatingPlayer(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Player</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={newPlayer.firstName || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={newPlayer.lastName || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, lastName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Position</label>
                <input
                  type="text"
                  value={newPlayer.position || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, position: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Jersey Number</label>
                <input
                  type="number"
                  value={newPlayer.jerseyNumber || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, jerseyNumber: parseInt(e.target.value) || undefined})}
                />
              </div>
              <div className="form-group">
                <label>Height</label>
                <input
                  type="text"
                  value={newPlayer.height || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, height: e.target.value})}
                  placeholder="e.g., 5'6&quot;"
                />
              </div>
              <div className="form-group">
                <label>Weight</label>
                <input
                  type="text"
                  value={newPlayer.weight || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, weight: e.target.value})}
                  placeholder="e.g., 120 lbs"
                />
              </div>

              <div className="form-section-header">
                <h3>Photo Upload</h3>
              </div>
              <div className="form-group" style={{gridColumn: '1 / -1'}}>
                <label>Player Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewPlayerPhoto(e.target.files?.[0] || null)}
                />
                {newPlayerPhoto && (
                  <small style={{color: '#059669', marginTop: '5px', display: 'block'}}>
                    ✓ Photo selected: {newPlayerPhoto.name}
                  </small>
                )}
              </div>

              <div className="form-section-header">
                <h3>Team Memberships *</h3>
              </div>
              <div className="form-group" style={{gridColumn: '1 / -1'}}>
                <div style={{marginBottom: '10px', padding: '10px', background: selectedTeamIds.length > 0 ? '#d4edda' : '#f8d7da', borderRadius: '6px'}}>
                  <strong style={{color: selectedTeamIds.length > 0 ? '#155724' : '#721c24'}}>
                    {selectedTeamIds.length > 0 ? `✓ ${selectedTeamIds.length} team(s) selected` : '✗ At least one team required'}
                  </strong>
                  {selectedTeamIds.length > 0 && (
                    <div style={{fontSize: '13px', marginTop: '5px', color: '#155724'}}>
                      {allTeams.filter(t => selectedTeamIds.includes(t.id)).map(t => t.name).join(', ')}
                    </div>
                  )}
                </div>
                <label>Select Teams (check all that apply)</label>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginTop: '10px'}}>
                  {allTeams.map(team => (
                    <label key={team.id} style={{display: 'flex', alignItems: 'center', padding: '8px', background: '#f5f5f5', borderRadius: '4px', cursor: 'pointer'}}>
                      <input
                        type="checkbox"
                        checked={selectedTeamIds.includes(team.id)}
                        onChange={() => handleTeamToggle(team.id)}
                        style={{marginRight: '8px'}}
                      />
                      <span>{team.name}</span>
                      {team.ageGroup && <span style={{marginLeft: 'auto', fontSize: '12px', color: '#666'}}>({team.ageGroup})</span>}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section-header">
                <h3>Guardian 1</h3>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newPlayer.guardian1Name || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, guardian1Name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newPlayer.guardian1Email || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, guardian1Email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={newPlayer.guardian1Phone || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, guardian1Phone: e.target.value})}
                />
              </div>

              <div className="form-section-header">
                <h3>Guardian 2</h3>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newPlayer.guardian2Name || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, guardian2Name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newPlayer.guardian2Email || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, guardian2Email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={newPlayer.guardian2Phone || ''}
                  onChange={(e) => setNewPlayer({...newPlayer, guardian2Phone: e.target.value})}
                />
              </div>
            </div>
            
            <div className="modal-actions">
              <button onClick={() => setCreatingPlayer(false)} className="btn-cancel">Cancel</button>
              <button onClick={async () => {
                // Validation
                if (!newPlayer.firstName || !newPlayer.lastName) {
                  alert('First and Last name are required');
                  return;
                }
                if (selectedTeamIds.length === 0) {
                  alert('At least one team must be selected');
                  return;
                }

                try {
                  // Create player with team IDs
                  const createData = {
                    ...newPlayer,
                    teamIds: selectedTeamIds,
                  };
                  
                  const response = await playersAPI.create(createData);
                  const createdPlayerId = response.data.id;

                  // Upload photo if provided
                  if (newPlayerPhoto && createdPlayerId) {
                    await playersAPI.uploadPhoto(createdPlayerId, newPlayerPhoto);
                  }

                  await loadPlayers();
                  setCreatingPlayer(false);
                  setNewPlayer({});
                  setNewPlayerPhoto(null);
                  setSelectedTeamIds([]);
                  alert('Player created successfully!');
                } catch (error) {
                  console.error('Failed to create player:', error);
                  alert('Failed to create player');
                }
              }} className="btn-save">Create Player</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingPlayer && (
        <div className="modal-overlay" onClick={() => setEditingPlayer(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Player</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={editingPlayer.firstName}
                  onChange={(e) => setEditingPlayer({...editingPlayer, firstName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={editingPlayer.lastName}
                  onChange={(e) => setEditingPlayer({...editingPlayer, lastName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Position</label>
                <input
                  type="text"
                  value={editingPlayer.position || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, position: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Jersey Number</label>
                <input
                  type="number"
                  value={editingPlayer.jerseyNumber || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, jerseyNumber: parseInt(e.target.value) || undefined})}
                />
              </div>
              <div className="form-group">
                <label>Height</label>
                <input
                  type="text"
                  value={editingPlayer.height || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, height: e.target.value})}
                  placeholder="e.g., 5'6&quot;"
                />
              </div>
              <div className="form-group">
                <label>Weight</label>
                <input
                  type="text"
                  value={editingPlayer.weight || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, weight: e.target.value})}
                  placeholder="e.g., 120 lbs"
                />
              </div>

              <div className="form-section-header">
                <h3>Player Type</h3>
              </div>
              <div className="form-group" style={{gridColumn: '1 / -1'}}>
                <label>Player Type *</label>
                <select
                  value={editingPlayer.playerType || 'Competition'}
                  onChange={(e) => setEditingPlayer({...editingPlayer, playerType: e.target.value})}
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    width: '100%'
                  }}
                >
                  <option value="Competition">Competition</option>
                  <option value="Recreational">Recreational</option>
                </select>
                <small style={{color: '#666', marginTop: '5px', display: 'block'}}>
                  <strong>Competition:</strong> Full access to rankings, evaluations, and all features<br/>
                  <strong>Recreational:</strong> Can only view their own stats (no team rankings or evaluations)
                </small>
              </div>

              <div className="form-section-header">
                <h3>Team Memberships</h3>
              </div>
              <div className="form-group" style={{gridColumn: '1 / -1'}}>
                <div style={{marginBottom: '10px', padding: '10px', background: '#e3f2fd', borderRadius: '6px'}}>
                  <strong style={{color: '#1565c0'}}>
                    Current Teams: {selectedTeamIds.length > 0 ? selectedTeamIds.length : 'None'}
                  </strong>
                  {selectedTeamIds.length > 0 && (
                    <div style={{fontSize: '13px', marginTop: '5px', color: '#1565c0'}}>
                      {allTeams.filter(t => selectedTeamIds.includes(t.id)).map(t => t.name).join(', ')}
                    </div>
                  )}
                </div>
                <label>Select Teams (check all that apply)</label>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginTop: '10px'}}>
                  {allTeams.map(team => (
                    <label key={team.id} style={{display: 'flex', alignItems: 'center', padding: '8px', background: '#f5f5f5', borderRadius: '4px', cursor: 'pointer'}}>
                      <input
                        type="checkbox"
                        checked={selectedTeamIds.includes(team.id)}
                        onChange={() => handleTeamToggle(team.id)}
                        style={{marginRight: '8px'}}
                      />
                      <span>{team.name}</span>
                      {team.ageGroup && <span style={{marginLeft: 'auto', fontSize: '12px', color: '#666'}}>({team.ageGroup})</span>}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section-header">
                <h3>Guardian 1</h3>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editingPlayer.guardian1Name || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, guardian1Name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingPlayer.guardian1Email || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, guardian1Email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editingPlayer.guardian1Phone || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, guardian1Phone: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Guardian 1 Password</label>
                <input
                  type="password"
                  value={editingPlayer.guardian1Password || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, guardian1Password: e.target.value})}
                  placeholder="Leave blank to keep current password"
                />
              </div>

              <div className="form-section-header">
                <h3>Guardian 2</h3>
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={editingPlayer.guardian2Name || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, guardian2Name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editingPlayer.guardian2Email || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, guardian2Email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editingPlayer.guardian2Phone || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, guardian2Phone: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Guardian 2 Password</label>
                <input
                  type="password"
                  value={editingPlayer.guardian2Password || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, guardian2Password: e.target.value})}
                  placeholder="Leave blank to keep current password"
                />
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => handleDelete(editingPlayer)} 
                className="btn-cancel"
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  marginRight: 'auto'
                }}
              >
                🗑️ Delete Player
              </button>
              <button onClick={() => setEditingPlayer(null)} className="btn-cancel">Cancel</button>
              <button onClick={handleSave} className="btn-save">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Players;
